import { useMutation } from '@apollo/client/react';
import { type BasicCouponFragment, ValidateCouponDocument } from '@drycut/graphql';
import {
  Button,
  CircleButton,
  FormHelpText,
  InfoButton,
  LoadingBar,
  Para,
  Stack,
  TextField,
} from '@drycut/ui-components';
import { TagIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { useCheckoutStore } from '../BookingCheckout/checkoutStore';
import CouponMessage from './CouponMessage';

type Props = {
  disabled?: boolean;
  onValidate: (coupon: BasicCouponFragment) => void;
};

// TODO: Maybe rename after we remove the CouponInsert component?
export function NewCouponInsert({ onValidate, disabled = false }: Props): React.ReactElement {
  const { setError, clearErrors, resetField } = useFormContext();
  const selectedCoupon = useCheckoutStore((state) => state.selectedCoupon);
  const setSelectedCoupon = useCheckoutStore((state) => state.setSelectedCoupon);
  const [searchParams] = useSearchParams();
  const phoneNumber = useWatch({ name: 'phone' });

  const code = searchParams.get('coupon');

  const [validateCoupon, { loading }] = useMutation(ValidateCouponDocument);

  // Validate coupon code in the query string (present when the customer comes from a referral link)
  useEffect(() => {
    if (code) {
      validateCoupon({
        variables: {
          input: {
            phoneNumber,
            code,
          },
        },
      }).then(({ data }) => {
        const validatedCoupon = data?.validateCoupon?.coupon;

        if (validatedCoupon) {
          setSelectedCoupon(validatedCoupon);
          onValidate(validatedCoupon);
        }
      });
    }
  }, [code, phoneNumber, validateCoupon, onValidate, setSelectedCoupon]);

  const validateCode = async (couponCode: string) => {
    clearErrors('couponCode');

    const { data } = await validateCoupon({
      variables: {
        input: {
          phoneNumber,
          code: couponCode,
        },
      },
    });

    const validatedCoupon = data?.validateCoupon?.coupon;
    const errors = data?.validateCoupon?.errors || [];

    if (validatedCoupon) {
      setSelectedCoupon(validatedCoupon);
      onValidate(validatedCoupon);
    } else {
      setError('couponCode', { message: errors[0].message, type: 'manual' }, { shouldFocus: true });
    }
  };

  const handleRemove = () => {
    resetField('couponCode');
    clearErrors('couponCode');
    setSelectedCoupon(undefined);
  };

  if (selectedCoupon) {
    return (
      <Stack gap={1}>
        <Stack direction="row" gap={2} className="items-center">
          <Stack direction="row" gap={2}>
            <TagIcon className="w-4 h-4 text-primary-600" />
            <Para className="text-primary-600" weight="medium">
              {selectedCoupon.code}
            </Para>
          </Stack>

          <CircleButton onClick={handleRemove} className="text-zinc-500">
            <XMarkIcon className="size-4" />
          </CircleButton>
        </Stack>

        <CouponMessage couponInfo={selectedCoupon} />
      </Stack>
    );
  }

  return (
    <Controller
      name="couponCode"
      render={({ field, fieldState: { invalid, error } }) => (
        <Stack gap={1}>
          <Stack direction="row" gap={2} className="items-end">
            <TextField
              {...field}
              label={
                <Stack direction="row" gap={1} className="items-center">
                  <Para as="span" size="xs" weight="medium">
                    Rabatt / DRYPASS
                  </Para>

                  <InfoButton
                    title="Rabattcode"
                    text="Rabattcodes gelten ausschliesslich für Haarschnitte und können nicht nachträglich abgezogen werden."
                    className="z-20"
                  />
                </Stack>
              }
              autoComplete="off"
              invalid={invalid}
              disabled={disabled || loading}
              fullWidth={false}
              className="grow"
              align="left"
              labelClassName="text-xs font-aribau text-left"
              placeholder="Code + Einlösen →"
            />

            <Button
              type="button"
              fill="outlined"
              size="small"
              onClick={() => validateCode(field.value)}
              disabled={disabled || loading || field.value.length < 3}
            >
              Einlösen
            </Button>
          </Stack>

          <LoadingBar loading={loading} />
          <FormHelpText error={invalid}>{error?.message}</FormHelpText>
        </Stack>
      )}
    />
  );
}
